import { useState, useCallback, type Dispatch, type SetStateAction } from 'react'

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

function dateReviver(_key: string, value: unknown): unknown {
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    return new Date(value)
  }
  return value
}

export function getLocalStorageValue<T>(key: string, defaultValue: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return defaultValue
    return JSON.parse(raw, dateReviver) as T
  } catch {
    return defaultValue
  }
}

export function setLocalStorageValue<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage full or unavailable — silently ignore
  }
}

export function useLocalStorage<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValueState] = useState<T>(() => getLocalStorageValue(key, defaultValue))

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (action) => {
      setValueState((prev) => {
        const next = action instanceof Function ? action(prev) : action
        setLocalStorageValue(key, next)
        return next
      })
    },
    [key]
  )

  return [value, setValue]
}
